// SPDX-FileCopyrightText: Copyright (c) 2025 Software GmbH, Darmstadt, Germany and/or its subsidiaries and/or its affiliates
// SPDX-FileContributor: Hendrik Siemund
// SPDX-FileContributor: Martin Heß
// SPDX-FileContributor: Zead Alshukairi (planen-bauen 4.0 GmbH)
// SPDX-FileContributor: Miguel Vega (planen-bauen 4.0 GmbH)
//
// SPDX-License-Identifier: Apache-2.0

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DIDServiceDescription, DIDStatus, IDIDService } from './did-service.interface';
import { DidGeneratorOutput2, DIDMethodDescription } from '../models';
import { DidWebService } from "./did-web.service";

@Injectable({
  providedIn: 'root'
})
export class DIDManagementService {

  /**
   * The DID Service registry containing DID management services for different DID methods
   */
  private methods: Map<string, IDIDService> = new Map<string, IDIDService>();


  constructor(web: DidWebService) {
    this.registerDIDMethod(web);
  }

  /**
   * Returns the list of currently supported DID methods.
   *
   * @returns the currently supported DID methods
   */
  getSupportedDIDMethods(): DIDServiceDescription[] {
    const arr: DIDServiceDescription[] = [];
    this.methods.forEach((value: IDIDService, key: string) => arr.push(value.getDescription()));
    return arr;
  }

  /**
   * Returns the list of DID methods that can be selected in the UI.
   *
   * @returns Observable emitting the available DID method descriptions
   */
  getAvailableMethods(): Observable<DIDMethodDescription[]> {
    const methods: DIDMethodDescription[] = this.getSupportedDIDMethods()
      .map((description: DIDServiceDescription) => ({
        label: description.label,
        method: description.method,
        link: description.link
      }));

    return of(methods);
  }

  /**
   * Checks if the specified DID method is supported.
   *
   * @param method the DID method
   * @returns true if the method is supported, otherwise false
   */
  supportsDIDMethod(method: string): boolean {
    return this.methods.has(method);
  }

  /**
   * Registers a new {@link IDIDService}.
   *
   * @param service the DID Service
   */
  registerDIDMethod(service: IDIDService): void {
    console.debug("Registering DID method", service.getDescription());
    this.methods.set(service.getDescription().method, service);
  }

  /**
   * Returns the description of the DID method for the specified DID.
   *
   * @param did the DID
   * @returns the description of the DID method or undefined if the method is not supported
   */
  getDIDMethodDescription(did: string): DIDServiceDescription | undefined {
    const method: string = this.extractDIDMethod(did);
    const didService: IDIDService | undefined = this.methods.get(method);
    if (!didService){
      return undefined;
    }

    return didService.getDescription();
  }


  /**
   * Returns the DID method string from the specified DID.
   *
   * @param did the DID
   * @returns the DID method string
   */
  extractDIDMethod(did: string): string {
    const tmp: string[] = did.split(':');
    const method: string = tmp[1];
    return method;
  }

  /**
   * Generates a new DID using the specified method and parameters.
   *
   * @param method the DID method
   * @param params the required generation parameters
   * @returns an {@link Observable} containing the created DID Document as string
   */
  generateDID(method: string, params: any, ownerId: number): Observable<{ status: string, message: string, value: DidGeneratorOutput2 | null }> {
    const didService: IDIDService | undefined = this.methods.get(method);
    if (!didService){
      throw new Error('Cannot generate DID. The DID method \'' + method + '\' is not supported.');
    }

    return didService.generateDID(params, ownerId);
  }

  /**
   * Resolves the specified DID and returns the corrsponding DID Document as string.
   *
   * @param did the DID
   * @returns an {@link Observable} containing the DID Document as string
   */
  resolveDID(did: string): Observable<string> {
    const method: string = this.extractDIDMethod(did);
    const didService: IDIDService | undefined = this.methods.get(method);
    if (!didService){
      throw new Error('Cannot resolve DID \'' + did + ' \'. The DID method \'' + method + '\' is not supported.');
    }

    return didService.resolveDID(did);
  }

  /**
   * Revokes the specified DID.
   *
   * @param did the DID
   * @param params The forwarded params
   * @returns an {@link Observable} that completes when the DID revocation is done
   */
  revokeDID(did: string, params?: any): Observable<any> {
    const method: string = this.extractDIDMethod(did);
    const didService: IDIDService | undefined = this.methods.get(method);

    if (!didService){
      throw new Error('Cannot revoke DID \'' + did + ' \'. The DID method \'' + method + '\' is not supported.');
    }

    if(!didService.getDescription().supportsDIDRevocation){
      return of();
    }

    return didService.revokeDID(did, params);
  }

  /**
   * Deactivates the specified DID.
   *
   * @param did the DID
   * @param params the forwarded params
   * @returns an {@link Observable} that completes when the DID deactivation is done
   */
  deactivateDID(did: string, params?: any): Observable<any> {
    const method: string = this.extractDIDMethod(did);
    const didService: IDIDService | undefined = this.methods.get(method);

    if (!didService){
      throw new Error('Cannot deactivate DID \'' + did + ' \'. The DID method \'' + method + '\' is not supported.');
    }

    if(!didService.getDescription().supportsDIDDeactivation){
      return of();
    }

    return didService.deactivateDID(did, params);
  }

  /**
   * Activates the specified DID.
   *
   * @param did the DID
   * @param params the forwarded params
   * @returns an {@link Observable} that completes when the DID activation is done
   */
  activateDID(did: string, params?: any): Observable<any> {
    const method: string = this.extractDIDMethod(did);
    const didService: IDIDService | undefined = this.methods.get(method);

    if (!didService){
      throw new Error('Cannot activate DID \'' + did + ' \'. The DID method \'' + method + '\' is not supported.');
    }

    if(!didService.getDescription().supportsDIDActivation){
      return of();
    }

    return didService.activateDID(did, params);
  }


  /**
   * Checks if DID is deactivated.
   *
   * @param did the DID
   * @returns an Observable holding the deactivation status
   */
  getDIDStatus(did: string): Observable<DIDStatus> {
    const method: string = this.extractDIDMethod(did);
    const didService: IDIDService | undefined = this.methods.get(method);

    if (!didService){
      throw new Error('Cannot get status of DID \'' + did + ' \'. The DID method \'' + method + '\' is not supported.');
    }

    return didService.getDIDStatus(did);
  }

}
